import { creatorInitials } from "../../../shared/src/creator-initials";
import type { SiteShell } from "../server/shell";
import { PoweredByDrate } from "./drate-wordmark";
import { sitePath } from "./site-header";
import { CurrencyPicker } from "./currency-picker";
import { SocialLinks } from "./social-links";

/** The creator's footer: brand or copyright, socials, policy pages and note. */
export function SiteFooter({ shell }: { shell: SiteShell }) {
  const { creator, footer, header } = shell;
  if (footer.style !== "simple" && footer.style !== "brand") return <ThemeFooter shell={shell} />;
  return (
    <footer className="site-footer">
      <div className="container footer-inner">
        {footer.style === "brand" ? (
          <div className="footer-brand">
            {creator.logoUrl ? (
              <img src={creator.logoUrl} alt="" width="28" height="28" loading="lazy" />
            ) : (
              <span className="brand-initials" aria-hidden="true">
                {creatorInitials(header.name)}
              </span>
            )}
            <div>
              {/* The logo's name, as the header shows it; copyright lines keep the site's. */}
              <p className="footer-name">{header.name}</p>
              {footer.tagline && <p className="footer-tagline">{footer.tagline}</p>}
            </div>
          </div>
        ) : (
          <p className="footer-copyright">
            {`${creator.name} `}
            <span>{`© ${new Date().getFullYear()}`}</span>
          </p>
        )}
        {footer.socialIcons ? (
          <SocialLinks socials={creator.socials} className="footer-socials" />
        ) : (
          creator.socials.length > 0 && (
            <nav aria-label="Footer">
              {creator.socials.map((item) => (
                <a key={item.url} href={item.url} target="_blank" rel="noopener noreferrer">
                  {item.label}
                </a>
              ))}
            </nav>
          )
        )}
        {footer.policies.length > 0 && (
          <nav aria-label="Policies">
            {footer.policies.map((page) => (
              <a key={page.path} href={sitePath(shell, page.path)}>
                {page.label}
              </a>
            ))}
          </nav>
        )}
        {footer.note && <p className="footer-note">{footer.note}</p>}
        {footer.poweredBy && <PoweredByDrate />}
      </div>
    </footer>
  );
}

/**
 * A storefront theme's footer (Editor v2): the policy menu, the socials and a copyright line,
 * placed by its alignment (header-presets.css). Modern leads with the logo or the name.
 */
function ThemeFooter({ shell }: { shell: SiteShell }) {
  const { creator, footer, header } = shell;
  return (
    <footer className="site-footer theme-footer">
      <div className="container footer-grid">
        {footer.style === "modern" && (
          <a className="footer-mark" href={sitePath(shell, "/")} aria-label="Homepage">
            {creator.logoUrl && <img src={creator.logoUrl} alt="" loading="lazy" />}
            <span>{header.name}</span>
          </a>
        )}
        {footer.policies.length > 0 && (
          <nav className="footer-menu" aria-label="Policies">
            {footer.policies.map((page) => (
              <a key={page.path} href={sitePath(shell, page.path)}>
                {page.label}
              </a>
            ))}
          </nav>
        )}
        <SocialLinks socials={creator.socials} className="footer-socials" />
        <div className="footer-legal">
          <div className="footer-meta">
            <p>{footer.note || `© ${new Date().getFullYear()} ${creator.name}`}</p>
            {footer.tagline && <p>{footer.tagline}</p>}
          </div>
          <div className="footer-utilities">
            {footer.currency && (
              <CurrencyPicker
                creatorId={creator.id}
                currency={footer.currency}
                id="footer-currency"
              />
            )}
            {footer.poweredBy && <PoweredByDrate />}
          </div>
        </div>
      </div>
    </footer>
  );
}
